import { useState } from 'react'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { LanguageSwitcher } from '@/app/LanguageSwitcher'
import { identityQuery, useLogin } from '@/features/auth/auth.queries'
import { signInMessage } from '@/features/auth/signInMessage'
import { Button, Card, TextField } from '@/ui'

const searchSchema = z.object({
  /** Where to return to after signing in. Set by the authenticated layout's guard. */
  redirect: z.string().optional(),
})

export const Route = createFileRoute('/login')({
  validateSearch: searchSchema,
  beforeLoad: async ({ context, search }) => {
    // Already signed in? Skip the form. A 401 here is the expected answer, not an error.
    try {
      await context.queryClient.query({ ...identityQuery, staleTime: 'static' })
      throw redirect({ to: search.redirect ?? '/' })
    } catch (error) {
      // `redirect()` throws, and rethrowing it is how the router performs the navigation.
      if (error instanceof Error && 'to' in error) throw error
    }
  },
  component: LoginPage,
})

const credentialsSchema = z.object({
  email: z.email(),
  password: z.string().min(1),
})

function LoginPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const search = Route.useSearch()
  const login = useLogin()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; password?: string }>({})

  function handleSubmit(event: React.SyntheticEvent) {
    event.preventDefault()

    const parsed = credentialsSchema.safeParse({ email, password })
    if (!parsed.success) {
      const flattened = z.flattenError(parsed.error)
      setFieldErrors({
        ...(flattened.fieldErrors.email
          ? { email: email ? t('auth.emailInvalid') : t('auth.emailRequired') }
          : {}),
        ...(flattened.fieldErrors.password ? { password: t('auth.passwordRequired') } : {}),
      })
      return
    }

    setFieldErrors({})
    login.mutate(parsed.data, {
      onSuccess: () => {
        void navigate({ to: search.redirect ?? '/' })
      },
    })
  }

  return (
    <main id="main" className="grid min-h-dvh place-items-center bg-background p-6">
      <Card className="w-full max-w-sm">
        <div className="mb-6 text-center">
          <h1 className="text-lg leading-7 font-semibold text-text">{t('auth.title')}</h1>
          <p className="mt-1 text-[0.8125rem] leading-5 text-text-secondary">{t('auth.subtitle')}</p>
        </div>

        <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
          <TextField
            label={t('auth.email')}
            type="email"
            autoComplete="username"
            autoFocus
            required
            value={email}
            error={fieldErrors.email}
            onChange={(event) => { setEmail(event.target.value) }}
          />

          <TextField
            label={t('auth.password')}
            type="password"
            autoComplete="current-password"
            required
            value={password}
            error={fieldErrors.password}
            onChange={(event) => { setPassword(event.target.value) }}
          />

          {login.isError ? (
            // assertive: the person is waiting on exactly this answer.
            <p role="alert" className="text-[0.8125rem] leading-5 text-danger">
              {signInMessage(login.error, t)}
            </p>
          ) : null}

          <Button type="submit" variant="primary" size="lg" isFullWidth isLoading={login.isPending}>
            {login.isPending ? t('auth.signingIn') : t('auth.signIn')}
          </Button>
        </form>

        {/*
          The switcher has to exist here as well as in the sidebar. The sidebar only appears once
          somebody is signed in, and somebody who cannot read this page is exactly the person who
          needs to change the language before they can get past it.
        */}
        <div className="mt-6 flex justify-center border-t border-border pt-4">
          <LanguageSwitcher />
        </div>
      </Card>
    </main>
  )
}
